import { maybe } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import zipObject from 'lodash/zipObject';
import { Booking } from '../../../../__types__';
import { V1Action } from '../../../../v4/migration/actionAsEventMiddleware';
import { createBatcher } from '../../../../v4/entities/common/batcher';
import { BookingApi } from '../../../api/booking/booking.api';
import { booking$ } from '../../../api/booking/booking.api.http.schema';
import { bookingQueryUtils } from '../../../api/booking/booking.api';
import { rowLevelValidation } from '../../../platform/row-level-validation';
import { z } from 'zod';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { BookingService } from './BookingService';
import { BookingStatisticsApi } from '../../../api/booking-statistics/booking-statistics.api';
import { normalizeQuery } from '../../../api/_common/query/queryUtils';

export function createBookingService(config: {
  api: BookingApi;
  statisticsApi: BookingStatisticsApi;
  queryClient: QueryClient;
  v1Event: SimpleReadOnlyEvent<V1Action>;
  services: WithErrorService & WithMessageService;
  isAuthenticated: () => boolean;
}): BookingService {
  const {
    api,
    queryClient,
    v1Event,
    services: { messageService, errorService }
  } = config;
  const interceptor = errorService.withCategory('BookingService');

  const batcher = createBatcher<Booking['id'], Booking[], Booking>(
    async (ids, signal) => {
      if (ids.length === 0) {
        return [];
      }
      return await interceptor.wrap(() => api.getItems(ids))();
    },
    (response, id) => {
      return maybe.getOrThrow(
        maybe.of(response.find(booking => booking.id === id)),
        `Booking with id ${id} not found`
      );
    }
  );

  const invalidateAll = () => {
    queryClient.removeQueries({
      queryKey: ['booking'],
      type: 'inactive'
    });
    return queryClient.invalidateQueries({
      queryKey: ['booking'],
      type: 'active'
    });
  };

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_BOOKING_SUCCESS':
      case 'UPDATE_BOOKING_SUCCESS':
      case 'DELETE_BOOKING_SUCCESS':
      case 'CREATE_INVOICES_SUCCESS':
      case 'UPDATE_INVOICE_SUCCESS':
      case 'DELETE_INVOICE_SUCCESS':
      case 'CREATE_EXPENSE_SUCCESS':
      case 'UPDATE_EXPENSE_SUCCESS':
      case 'DELETE_EXPENSE_SUCCESS':
      case 'CREATE_EXPENSE_RECEIPT_SUCCESS':
      case 'UPDATE_EXPENSE_RECEIPT_SUCCESS':
      case 'DELETE_EXPENSE_RECEIPT_SUCCESS':
        await invalidateAll();
        return;
      case 'GET_BOOKINGS_SUCCESS':
        // Cache bookings from list results
        if (event.result?.results) {
          const validationResult = z
            .array(booking$)
            .safeParse(event.result.results);
          if (validationResult.success) {
            // All bookings are valid, cache them all
            for (const booking of validationResult.data) {
              queryClient.setQueryData(
                ['booking', 'detail', booking.id],
                booking
              );
            }
          } else {
            // Some or all bookings are invalid, skip caching entirely
            console.warn(
              'Skipping cache fill for GET_BOOKINGS_SUCCESS due to invalid bookings:',
              validationResult.error
            );
          }
        }
        return;
      case 'GET_BOOKING_SUCCESS':
        // Cache single booking result
        if (event.result?.id) {
          const validationResult = booking$.safeParse(event.result);
          if (validationResult.success) {
            queryClient.setQueryData(
              ['booking', 'detail', event.result.id],
              validationResult.data
            );
          } else {
            console.warn(
              'Skipping invalid booking from v1 GET_BOOKING_SUCCESS:',
              event.result.id,
              validationResult.error
            );
          }
        }
        return;
      case 'GET_BATCH_BOOKINGS_SUCCESS':
        // Cache bookings from batch results
        if (event.result?.data) {
          const validationResult = z
            .array(booking$)
            .safeParse(event.result.data);
          if (validationResult.success) {
            // All bookings are valid, cache them all
            for (const booking of validationResult.data) {
              queryClient.setQueryData(
                ['booking', 'detail', booking.id],
                booking
              );
            }
          } else {
            // Some or all bookings are invalid, skip caching entirely
            console.warn(
              'Skipping cache fill for GET_BATCH_BOOKINGS_SUCCESS due to invalid bookings:',
              validationResult.error
            );
          }
        }
        return;
      case 'GET_CONTACT_ASSOCIATIONS_SUCCESS':
        // Cache bookings from contact associations
        const contactBookings = event.result?.bookings || [];
        const validationResult = z.array(booking$).safeParse(contactBookings);
        if (validationResult.success) {
          // All bookings are valid, cache them all
          for (const booking of validationResult.data) {
            queryClient.setQueryData(
              ['booking', 'detail', booking.id],
              booking
            );
          }
        } else {
          // Some or all bookings are invalid, skip caching entirely
          console.warn(
            'Skipping cache fill for GET_CONTACT_ASSOCIATIONS_SUCCESS due to invalid bookings:',
            validationResult.error
          );
        }
        return;
    }
  });

  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'booking':
          await invalidateAll();
          break;
      }
    }
    message.sendResponse();
  });

  // Subscribe to cache events for guest shortlist data
  messageService.reportCache.addListener(event => {
    for (const entity of event.entities) {
      switch (entity.entity) {
        case 'booking':
          queryClient.setQueryData(
            ['booking', 'detail', entity.payload.id],
            entity.payload
          );
          break;
      }
    }
  });

  return {
    useStatistics: query => {
      return useQuery(
        {
          queryKey: ['booking-statistics', query],
          refetchInterval: 1000 * 60 * 5,
          queryFn: interceptor.wrap(ctx =>
            config.statisticsApi
              .getData(query!, ctx.signal)
              .then(value => value)
          )
        },
        config.queryClient
      );
    },

    useBookingList: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: ['booking', 'list', maybe.map(query, normalizeQuery)],
            queryFn: interceptor.wrap(async () => {
              const result = await maybe.map(query, api.getList);
              // Populate detail cache with bookings from list response
              if (maybe.isPresent(result)) {
                for (const row of result.items) {
                  if (row.isValid) {
                    queryClient.setQueryData(
                      ['booking', 'detail', row.data.id],
                      row.data
                    );
                  }
                }
              }
              return result;
            }),
            initialData: query
              ? (() => {
                  // This is needed specifically to shortlist guest mode where we put shortlist related events to detail cache

                  if (config.isAuthenticated()) {
                    // Not in guest mode, don't provide initialData so API fetch can happen
                    return undefined;
                  }

                  // Check cache for bookings that match this query
                  const cachedBookings = queryClient.getQueriesData<Booking>({
                    queryKey: ['booking', 'detail']
                  });
                  const matchingBookings: Booking[] = [];
                  for (const [, booking] of cachedBookings) {
                    if (
                      booking &&
                      bookingQueryUtils.matchesQuery(query, booking)
                    ) {
                      matchingBookings.push(booking);
                    }
                  }

                  if (matchingBookings.length > 0) {
                    return {
                      items:
                        rowLevelValidation.mapToValidatedRows(matchingBookings),
                      count: matchingBookings.length
                    };
                  }

                  // In public mode but no matching bookings found - still return empty result
                  // to prevent infinite fetching
                  return {
                    items: [],
                    count: 0
                  };
                })()
              : undefined
          },
          queryClient
        )
      ),

    useBookingsById: ids => {
      // Always call useQueries (hooks must be called unconditionally)
      const results = useQueries(
        {
          queries: ids.map(id => ({
            queryKey: ['booking', 'detail', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              return await batcher.fetch({ payload: id, signal });
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000 // 10 minutes
          }))
        },
        queryClient
      );

      // Convert UseQueryResult to Record<id, RemoteData<Booking>>
      // UseQueryResult is directly assignable to RemoteData
      return zipObject(ids, results);
    },

    useBooking: id =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            enabled: maybe.isPresent(id),
            queryKey: ['booking', 'detail', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              if (!id) {
                throw new Error('Booking ID is required');
              }
              return await api.getItem(id);
            }
          },
          queryClient
        )
      ),

    useBookingConfirmationPreview: request =>
      ensureIdleQuery(
        request,
        useQuery(
          {
            enabled: maybe.isPresent(request),
            queryKey: [
              'booking',
              'confirmation-preview',
              request?.bookingId,
              request?.enterpriseWorkspaceId ?? null
            ],
            queryFn: () => api.previewBookingConfirmation(request!),
            retry: false
          },
          queryClient
        )
      ),

    ensureBooking: id =>
      queryClient.fetchQuery({
        queryKey: ['booking', 'detail', id],
        queryFn: interceptor.wrap(() => api.getItem(id))
      })
  };
}
